import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getBillTo, GetBillToApiParameter } from "@insite/client-framework/Services/CustomersService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { UnitOfMeasureModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { OptionObject } from "@insite/mobius/DynamicDropdown";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import Info from "@insite/mobius/Icons/Info";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Select from "@insite/mobius/Select";
import TextArea from "@insite/mobius/TextArea";
import TextField from "@insite/mobius/TextField";
import Toaster from "@insite/mobius/Toast/Toaster";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Tooltip from "@insite/mobius/Tooltip";
import Typography from "@insite/mobius/Typography";
import PhoneNumberField, { PhoneNumberState } from "@preferredMedical/Components/Fields/PhoneNumberField";
import { EventEx } from "@preferredMedical/Models/Misc/EventEx";
import { PriceRequestEmailRequest } from "@preferredMedical/Models/ProductDetails/PriceRequestEmailRequest";
import ReCaptcha from "@preferredMedical/Overrides/Components/ReCaptcha";
import { sendPriceRequestEmail } from "@preferredMedical/Services/ProductService";
import validateReCaptcha from "@preferredMedical/Store/Components/Recapcha/Handlers/ValidateReCaptcha";
import React, { RefObject } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface PriceRequestProps {
    emailRecipients: string;
    title: string;
    description: string;
    requiredFieldsLabel: string;
}

const mapStateToProps = (state: ApplicationState) => ({
    session: state.context.session,
});

const mapDispatchToProps = {
    validateReCaptcha: makeHandlerChainAwaitable<{ location: string; recaptchaRef: RefObject<ReCAPTCHA> }, boolean>(
        validateReCaptcha,
    ),
};

type Props = PriceRequestProps & ReturnType<typeof mapStateToProps> & HasProduct & ResolveThunks<typeof mapDispatchToProps>;

export interface PriceRequestStyles {
    modal?: ModalPresentationProps;
    container?: GridContainerProps;
    oneLinerGridItem?: GridItemProps;
    twoLinerGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
}

export interface PriceRequestStyles {
    modal?: ModalPresentationProps;
    container?: GridContainerProps;
    oneLinerGridItem?: GridItemProps;
    twoLinerGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    requestButton?: ButtonPresentationProps;
}

export const priceRequestStyles: PriceRequestStyles = {
    modal: {
        size: 800,
        cssOverrides: {
            modalTitle: css`
                border-bottom: 0;
                padding-bottom: 0px;
                justify-content: center;
            `,
            titleButton: css`
                display: none;
            `,
            modalContent: css`
                padding: 20px 30px;
            `,
        },
    },
    container: { gap: 10 },
    oneLinerGridItem: {
        width: 12,
        css: css`
            padding-left: 10px;
            justify-content: center;
        `,
    },
    twoLinerGridItem: {
        width: 6,
        css: css`
            padding-left: 10px;
        `,
    },
    submitButton: {
        css: css`
            width: 100%;
        `,
    },
    requestButton: {
        css: css`
            width: 80%;
            padding: 12px 0;
            font-weight: bold;
            font-size: 16px;
            background-color: #005580;
            color: white;
    
            &:hover {
                background-color: #003c5a;
            }
        `,
    },
};

interface PriceRequestState {
    styles: PriceRequestStyles;
    isAuthenticated: boolean;

    contactName: string;
    contactEmail: string;
    contactNumber: string;
    companyName: string;
    shippingAddress: string;
    hasPreferredMedicalAccount: string;
    quantity: number;
    unitOfMeasures: UnitOfMeasureModel[];
    selectedUom: string;
    requestPrice: string;
    additionalComments: string;
    contactNumberOnChange: EventEx;
    contactNumberUpdate: EventEx;

    contactNameError: string;
    contactEmailError: string;
    contactNumberError: string;
    companyNameError: string;
    shippingAddressError: string;
    hasPreferredMedicalAccountError: string;
    quantityError: string;
    requestPriceError: string;

    modalIsOpen: boolean;
    inProgress: boolean;
    shipToAddresses: OptionObject[];

    billToId?: string;
}

const phoneRegex = new RegExp("[0-9]{3}-[0-9]{3}-[0-9]{4}");
const emailRegex = new RegExp("\\w+([-+.']\\w+)*@\\w+([-.]\\w+)*\\.\\w+([-.]\\w+)*");
const priceRegex = new RegExp("^[0-9]*[.,]?[0-9]*$");

class PriceRequestModal extends React.Component<Props, PriceRequestState> {
    private recaptchaRef: RefObject<ReCAPTCHA>;
    constructor(props: Props) {
        super(props);
        this.state = {
            styles: priceRequestStyles,
            isAuthenticated: (props.session.isAuthenticated || props.session.rememberMe) && !props.session.isGuest,

            contactName: "",
            contactEmail: "",
            contactNumber: "",
            companyName: "",
            shippingAddress: "",
            hasPreferredMedicalAccount: "",
            quantity: 1,
            unitOfMeasures: props.product.unitOfMeasures ?? [],
            selectedUom: props.product.unitOfMeasures?.find(u => u.isDefault)?.unitOfMeasure
                || props.product.unitOfMeasures?.[0]?.unitOfMeasure
                || "",
            requestPrice: "",
            additionalComments: "",
            contactNumberOnChange: new EventEx(),
            contactNumberUpdate: new EventEx(),

            contactNameError: "",
            contactEmailError: "",
            contactNumberError: "",
            companyNameError: "",
            shippingAddressError: "",
            hasPreferredMedicalAccountError: "",
            quantityError: "",
            requestPriceError: "",

            modalIsOpen: false,
            inProgress: false,
            shipToAddresses: [],
            billToId: props.session.billToId ? props.session.billToId : "",
        };

        this.state.contactNumberOnChange.subscribe(async (phoneNumberState: PhoneNumberState) => {
            await this.setState({ contactNumber: phoneNumberState.value });
            await this.setState({ contactNumberError: phoneNumberState.error });
        });

        this.recaptchaRef = React.createRef<ReCAPTCHA>();
        this.getCompanyInformation = this.getCompanyInformation.bind(this);
        this.resetFormData = this.resetFormData.bind(this);
        this.modalCloseHandler = this.modalCloseHandler.bind(this);
        this.openModal = this.openModal.bind(this);
        this.checkForErrors = this.checkForErrors.bind(this);
        this.submitButtonClickHandler = this.submitButtonClickHandler.bind(this);
    }

    async getCompanyInformation() {
        if (!this.state.isAuthenticated || !this.state.billToId) {
            return;
        }

        const getBillToParams: GetBillToApiParameter = {
            billToId: this.state.billToId,
            expand: ["shipTos"],
        };

        const billToModel = await getBillTo(getBillToParams);

        if (billToModel.shipTos) {
            const shippingAddresses = billToModel.shipTos.filter(x => !x.isNew && !x.oneTimeAddress);
            const currentShipToAddresses = shippingAddresses.map<OptionObject>(x => ({
                optionText: x.fullAddress,
                optionValue: x.fullAddress,
                searchString: x.fullAddress,
            }));

            await this.setState({
                shippingAddress: currentShipToAddresses[0]?.optionValue ?? "",
                shipToAddresses: currentShipToAddresses,
            });
        }

        if (phoneRegex.test(billToModel.phone)) {
            await this.setState({ contactNumber: billToModel.phone });
        }

        await this.setState({ companyName: billToModel.companyName });
    }

    async resetFormData() {
        await this.setState({
            contactName: this.state.isAuthenticated ? `${this.props.session.firstName} ${this.props.session.lastName}` : "",
            contactEmail: this.state.isAuthenticated ? this.props.session.email : "",
            contactNumber: "",
            companyName: "",
            shippingAddress: "",
            hasPreferredMedicalAccount: "",
            quantity: 1,
            unitOfMeasures: this.props.product.unitOfMeasures ?? [],
            selectedUom:
                this.props.product.unitOfMeasures?.find(u => u.isDefault)?.unitOfMeasure
                || this.props.product.unitOfMeasures?.[0]?.unitOfMeasure
                || "",
            requestPrice: "",
            additionalComments: "",

            contactNameError: "",
            contactEmailError: "",
            contactNumberError: "",
            companyNameError: "",
            shippingAddressError: "",
            hasPreferredMedicalAccountError: "",
            quantityError: "",
            requestPriceError: "",

            inProgress: false,
        });

        await this.getCompanyInformation();
    }

    async openModal() {
        await this.resetFormData();
        this.setState({ modalIsOpen: true });
    }

    async modalCloseHandler() {
        await this.setState({ modalIsOpen: false });
    }

    async checkForErrors() {
        const requiredFieldMessage = translate("This is a required field.");
        const emailFieldMessage = translate("Enter a valid email address.");
        const priceFieldMessage = translate("Enter a valid price (e.g., 100 or 99.99).");
        let encounteredError = false;

        if (!emailRegex.test(this.state.contactEmail)) {
            await this.setState({ contactEmailError: emailFieldMessage });
            encounteredError = true;
        }

        if (!this.state.contactName || this.state.contactName === "") {
            await this.setState({ contactNameError: requiredFieldMessage });
            encounteredError = true;
        }

        if (!this.state.contactNumber || this.state.contactNumber === "") {
            await this.setState({ contactNumberError: requiredFieldMessage });
            encounteredError = true;
        }

        if (!this.state.companyName || this.state.companyName === "") {
            await this.setState({ companyNameError: requiredFieldMessage });
            encounteredError = true;
        }

        if (!this.state.shippingAddress || this.state.shippingAddress === "") {
            await this.setState({ shippingAddressError: requiredFieldMessage });
            encounteredError = true;
        }

        if (!this.state.hasPreferredMedicalAccount) {
            await this.setState({ hasPreferredMedicalAccountError: requiredFieldMessage });
            encounteredError = true;
        }        

        if (!this.state.quantity || this.state.quantity < 1) {
            await this.setState({ quantityError: requiredFieldMessage });
            encounteredError = true;
        }

        if (!priceRegex.test(this.state.requestPrice) || this.state.requestPrice.trim() === "") {
            await this.setState({ requestPriceError: priceFieldMessage });
            encounteredError = true;
        }        

        return encounteredError;
    }

    async submitButtonClickHandler(addToast: Function) {
        const successToast = {
            body: siteMessage("PriceRequestConfirmationMessage"),
            messageType: "info",
        };

        const dataHasErrors = await this.checkForErrors();

        if (dataHasErrors) {
            return;
        }

        const isCaptchaValid = await this.props.validateReCaptcha({
            location: "PriceRequest",
            recaptchaRef: this.recaptchaRef,
        });

        if (!isCaptchaValid) {
            addToast({
                body: siteMessage("PriceRequestReCaptchaError"),
                messageType: "danger",
            });
            return;
        }

        await this.setState({ inProgress: true });

        const model: PriceRequestEmailRequest = {
            contactName: this.state.contactName,
            contactEmail: this.state.contactEmail,
            contactNumber: this.state.contactNumber,
            companyName: this.state.companyName,
            shippingAddress: this.state.shippingAddress,
            hasPreferredMedicalAccount: this.state.hasPreferredMedicalAccount,
            productNumber: this.props.product.productNumber,
            productName: this.props.product.productTitle,
            quantity: this.state.quantity,
            unitOfMeasure: this.state.selectedUom,
            requestPrice: this.state.requestPrice,
            additionalComments: this.state.additionalComments,
            emailTos: this.props.emailRecipients !== undefined ? this.props.emailRecipients.split("//") : [],
        };

        sendPriceRequestEmail(model);
        addToast(successToast);
        this.setState({ modalIsOpen: false, inProgress: false });
    }

    render() {
        return (
            <>
                <Toaster>
                    <ToasterContext.Consumer>
                        {({ addToast }) => (
                            <>
                                <Button
                                    onClick={this.openModal}
                                    {...this.state.styles.requestButton}
                                    data-test-selector="priceRequestLink"
                                >
                                    {translate("Request Price")}
                                </Button>
                                <Modal
                                    {...this.state.styles.modal}
                                    headline={this.props.title}
                                    isOpen={this.state.modalIsOpen}
                                    handleClose={this.modalCloseHandler}
                                >
                                    <GridContainer {...this.state.styles.container} data-test-selector="priceRequestForm">
                                        <GridItem {...this.state.styles.oneLinerGridItem}>
                                            <Typography>{this.props.description}</Typography>
                                        </GridItem>
                                        <GridItem {...this.state.styles.twoLinerGridItem}>
                                            <TextField
                                                label={translate("Contact Name")}
                                                value={this.state.contactName}
                                                onChange={e => this.setState({ contactName: e.target.value, contactNameError: "" })}
                                                error={this.state.contactNameError}
                                                required
                                            />
                                        </GridItem>
                                        <GridItem {...this.state.styles.twoLinerGridItem}>
                                            <TextField
                                                label={translate("Contact Email")}
                                                value={this.state.contactEmail}
                                                onChange={async e => {
                                                    const value = e.target.value;
                                                    if (emailRegex.test(value)) {
                                                        await this.setState({ contactEmail: value, contactEmailError: "" });
                                                    } else {
                                                        await this.setState({ contactEmail: value });
                                                    }
                                                }}
                                                error={this.state.contactEmailError}
                                                required
                                            />
                                        </GridItem>
                                        <GridItem {...this.state.styles.twoLinerGridItem}>
                                            <PhoneNumberField
                                                label={translate("Contact Number")}
                                                initialValue={this.state.contactNumber}
                                                onChange={this.state.contactNumberOnChange}
                                                update={this.state.contactNumberUpdate}
                                                data-test-selector="pumpRentalContactNumber"
                                                format="000-000-0000"
                                                required={true}
                                                hideRequiredAsterisk={false}
                                            />
                                        </GridItem>
                                        <GridItem {...this.state.styles.twoLinerGridItem}>
                                            <TextField
                                                label={translate("Company Name")}
                                                value={this.state.companyName}
                                                onChange={e => this.setState({ companyName: e.target.value, companyNameError: "" })}
                                                error={this.state.companyNameError}
                                                required
                                            />
                                        </GridItem>
                                        <GridItem {...this.state.styles.oneLinerGridItem}>
                                            <TextField
                                                label={translate("Shipping Address")}
                                                value={this.state.shippingAddress}
                                                onChange={e => this.setState({ shippingAddress: e.target.value, shippingAddressError: "" })}
                                                error={this.state.shippingAddressError}
                                                required
                                            />
                                        </GridItem>
                                        <GridItem {...this.state.styles.oneLinerGridItem}>
                                            <Select
                                                label={translate("Do you have an existing Preferred Medical account?")}
                                                value={this.state.hasPreferredMedicalAccount}
                                                onChange={e => this.setState({ hasPreferredMedicalAccount: e.target.value, hasPreferredMedicalAccountError: "" })}
                                                error={this.state.hasPreferredMedicalAccountError}
                                                required
                                            >
                                                <option value="">Select</option>
                                                <option value="Yes">Yes</option>
                                                <option value="No">No</option>
                                            </Select>
                                        </GridItem>
                                        <GridItem {...this.state.styles.twoLinerGridItem}>
                                            <TextField
                                                label={translate("Product Item Number")}
                                                value={this.props.product.productNumber}
                                                disabled
                                            />
                                        </GridItem>
                                        <GridItem {...this.state.styles.twoLinerGridItem}>
                                            <TextField
                                                label={translate("Product Item Name")}
                                                value={this.props.product.productTitle}
                                                disabled
                                            />
                                        </GridItem>
                                        <GridItem {...this.state.styles.twoLinerGridItem}>
                                            <div style={{ display: "flex", gap: "10px" }}>
                                                <TextField
                                                    label={
                                                        <>
                                                            {translate("Quantity ")}
                                                            <Tooltip text={String(siteMessage("PriceRequestQuantityTooltip"))}>
                                                                <Icon src={Info} size={14} style={{ marginLeft: 6, cursor: "pointer" }} />
                                                            </Tooltip>
                                                        </>
                                                    }
                                                    type="number"
                                                    min={1}
                                                    value={this.state.quantity}
                                                    onChange={e =>
                                                        this.setState({ quantity: parseInt(e.target.value.replace(/\D/g, ""), 10) || 1, quantityError: "" })
                                                    }
                                                    error={this.state.quantityError}
                                                    required
                                                />

                                                {this.state.unitOfMeasures.length > 1 ? (
                                                    <Select
                                                        label={
                                                            <>
                                                                {translate("U/M ")}
                                                                <Tooltip text={String(siteMessage("PriceRequestUnitOfMeasureTooltip"))}>
                                                                    <Icon src={Info} size={14} style={{ marginLeft: 6, cursor: "pointer" }} />
                                                                </Tooltip>
                                                            </>
                                                        }
                                                        value={this.state.selectedUom}
                                                        onChange={e => this.setState({ selectedUom: e.target.value })}
                                                    >
                                                        {this.state.unitOfMeasures.map(uom => (
                                                            <option key={uom.unitOfMeasure} value={uom.unitOfMeasure}>
                                                                {uom.description || uom.unitOfMeasureDisplay || uom.unitOfMeasure}
                                                            </option>
                                                        ))}
                                                    </Select>
                                                ) : (
                                                    <TextField
                                                        label={
                                                            <>
                                                                {translate("U/M ")}
                                                                <Tooltip text={String(siteMessage("PriceRequestUnitOfMeasureTooltip"))}>
                                                                    <Icon src={Info} size={14} style={{ marginLeft: 6, cursor: "pointer" }} />
                                                                </Tooltip>
                                                            </>
                                                        }
                                                        value={this.state.unitOfMeasures[0]?.unitOfMeasureDisplay ?? this.state.selectedUom}
                                                        disabled
                                                    />
                                                )}
                                            </div>
                                        </GridItem>
                                        <GridItem {...this.state.styles.twoLinerGridItem}>
                                            <TextField
                                                label={
                                                    <>
                                                        {translate("Requested Price ")}
                                                        <Tooltip text={String(siteMessage("PriceRequestRequestedPriceTooltip"))}>
                                                            <Icon src={Info} size={14} style={{ marginLeft: 6, cursor: "pointer" }} />
                                                        </Tooltip>
                                                    </>
                                                }
                                                value={this.state.requestPrice}
                                                onChange={async e => {
                                                    const value = e.target.value;
                                                    if (priceRegex.test(value)) {
                                                        await this.setState({ requestPrice: value, requestPriceError: "" });
                                                    } else {
                                                        await this.setState({ requestPrice: value });
                                                    }
                                                }}                                                
                                                error={this.state.requestPriceError}
                                                required
                                            />
                                        </GridItem>
                                        <GridItem {...this.state.styles.oneLinerGridItem}>
                                            <TextArea
                                                label={translate("Additional Comments")}
                                                value={this.state.additionalComments}
                                                onChange={e => this.setState({ additionalComments: e.target.value })}
                                            />
                                        </GridItem>
                                        <GridItem {...this.state.styles.oneLinerGridItem}>
                                            <ReCaptcha recaptchaRef={this.recaptchaRef} location="PriceRequest" />
                                        </GridItem>
                                        <GridItem {...this.state.styles.oneLinerGridItem}>
                                            <Typography>{`* ${this.props.requiredFieldsLabel}`}</Typography>
                                        </GridItem>
                                        <GridItem {...this.state.styles.oneLinerGridItem}>
                                            <Button
                                                {...this.state.styles.submitButton}
                                                onClick={() => this.submitButtonClickHandler(addToast)}
                                                disabled={this.state.inProgress}
                                            >
                                                {translate("Submit")}
                                            </Button>
                                        </GridItem>
                                    </GridContainer>
                                </Modal>
                            </>
                        )}
                    </ToasterContext.Consumer>
                </Toaster>
            </>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withProduct(PriceRequestModal));